import type {
	Course,
	CourseSection as CourseSectionType,
	FocusSection,
} from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { CircleIcon, SparklesIcon, StarIcon } from "lucide-react";
import dynamic from "next/dynamic";
import { memo, useEffect, useMemo } from "react";

const CourseSectionsHashExpander = dynamic(
	// @ts-expect-error
	() => import("./CourseSectionsHashExpander"),
	{ ssr: false },
) as typeof import("./CourseSectionsHashExpander").default;

import useSWRImmutable from "swr/immutable";
import useLocalStorageState from "use-local-storage-state";

import CourseSection from "./CourseSection";
import CourseSectionsControls from "./CourseSectionsControls";
import {
	useAccess,
	useAuditedCourses,
	useCourseProgress,
	useRecommendedSections,
	useUserAction,
} from "~/hooks";
import useLatestExamAttemptSummary from "~/hooks/useLatestExamAttemptSummary";

export type { FocusSection } from "@brookbush/types";

interface CourseSectionsProps {
	course: Course;
}

const CourseSections = ({ course }: CourseSectionsProps) => {
	const { userId } = useAuth();

	const { userAction, setUserAction } = useUserAction();

	const { isMember } = useAccess();

	const { auditedCourses } = useAuditedCourses();

	const allSectionKeys = useMemo(
		() =>
			course.sections.reduce((acc, section) => {
				acc.push(section._key);

				if (section.subSections) {
					section.subSections.forEach((subSection) => {
						acc.push(subSection._key);
					});
				}

				return acc;
			}, [] as string[]),
		[course.sections],
	);

	const { actor } = useAuth();

	const auditedCourse = auditedCourses?.find(
		(auditedCourse) => auditedCourse.courseId === course._id,
	);

	const hasCourseAccess = isMember || !!auditedCourse;
	const canViewSections = !!(userId && hasCourseAccess);

	const [
		expandedSectionKeys = [
			allSectionKeys[0],
			course.sections.find((section) => section.title === "Bibliography")?._key,
		].filter(Boolean) as string[],
		setExpandedSections,
	] = useLocalStorageState<string[]>(
		actor
			? `course-${course._id}-expanded-sections-${actor.id}`
			: `course-${course._id}-expanded-sections`,
	);

	useEffect(() => {
		const findTriggered = (e: KeyboardEvent) => {
			if ((e.ctrlKey || e.metaKey) && e.key === "f" && hasCourseAccess) {
				setExpandedSections(allSectionKeys);
			}
		};
		window.addEventListener("keydown", findTriggered);

		return () => {
			window.removeEventListener("keydown", findTriggered);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allSectionKeys, hasCourseAccess]);

	const { progress } = useCourseProgress(course);

	const { data: visitedSectionKeys, mutate: mutateVisitedSections } =
		useSWRImmutable<string[]>(
			userId && `/courses/${course.slug}/visited/sections`,
		);

	const { data: latestSummary } = useLatestExamAttemptSummary(
		userId ? course.slug : undefined,
	);

	const { recommendedSections } = useRecommendedSections(
		userId ? course.slug : undefined,
	);

	// The tutor's recommendations win; until it has made any, the exam coach's still show
	const focusSections = useMemo<FocusSection[]>(
		() =>
			recommendedSections.length > 0
				? recommendedSections
				: (latestSummary?.focusSections ?? []),
		[recommendedSections, latestSummary],
	);

	const completeSectionKeys = useMemo(
		() => progress?.map((item) => item.sectionKey),
		[progress],
	);

	return (
		<div className="col-span-full laptop:col-span-7 flex flex-col gap-6">
			<div className="flex justify-between">
				<CourseSectionsControls
					expandedSections={expandedSectionKeys}
					setExpandedSections={setExpandedSections}
					allSectionKeys={allSectionKeys}
					hasCourseAccess={hasCourseAccess}
					courseId={course._id}
				/>

				<div className="phablet:mx-0 flex w-full phablet:max-w-max items-center gap-2 rounded-lg border px-2.5 py-1">
					<div className="flex items-center gap-1.5">
						<StarIcon className="relative inline-block h-4 phablet:h-4 phablet:w-4 w-4 shrink-0 rounded-full fill-orange-400 text-orange-400" />
						<p className="text-sm">Test Critical Content</p>
					</div>
					<div className="h-full border-r" />
					<div className="flex items-center gap-1.5">
						<SparklesIcon className="relative inline-block h-4 phablet:h-4 phablet:w-4 w-4 shrink-0 rounded-full fill-purple-400 text-purple-400" />
						<p className="text-sm">Coach Recommended Content</p>
					</div>
					<div className="h-full border-r" />
					<div className="flex items-center gap-1.5">
						<CircleIcon className="relative inline-block h-4 phablet:h-4 phablet:w-4 w-4 shrink-0 rounded-full" />
						<p className="text-sm">Mark As Complete</p>
					</div>
				</div>
			</div>

			<CourseSectionsHashExpander
				course={course}
				setExpandedSections={setExpandedSections}
			/>

			<div
				className="phablet:mx-0 flex flex-col gap-4 sm:gap-6"
				id="course-content"
			>
				{course.sections.map((section, i) => (
					<CourseSection
						course={course}
						isMember={isMember}
						userAction={userAction}
						setUserAction={setUserAction}
						key={section._key}
						section={section}
						mutateVisitedSections={mutateVisitedSections}
						visitedSectionKeys={visitedSectionKeys}
						completedSectionKeys={completeSectionKeys}
						expandedSectionKeys={expandedSectionKeys}
						focusSections={focusSections}
						isVisited={!!visitedSectionKeys?.includes(section._key)}
						isCompleted={!!completeSectionKeys?.includes(section._key)}
						isExpanded={
							(canViewSections ||
								i === 0 ||
								section.title === "Bibliography") &&
							expandedSectionKeys.includes(section._key)
						}
						hasCourseAccess={hasCourseAccess}
						isFreeSection={i === 0 || section.title === "Bibliography"}
						isFirstSection={i === 0}
						handleExpandSection={(section: CourseSectionType) => {
							setExpandedSections(expandedSectionKeys.concat(section._key));
						}}
						handleCollapseSection={(section: CourseSectionType) => {
							const subSectionKeys = section.subSections?.map(
								(subSection) => subSection._key,
							);

							setExpandedSections(
								expandedSectionKeys.filter(
									(expandedSection) =>
										expandedSection !== section._key &&
										!subSectionKeys?.includes(expandedSection),
								),
							);
						}}
						nextSection={course.sections[i + 1]}
					/>
				))}

				<p className="mt-2 phablet:text-lg">
					© {new Date().getFullYear()} Brookbush Institute. All rights reserved.
				</p>
			</div>
		</div>
	);
};

export default memo(CourseSections);
